import React from 'react'

import { FAQ } from '@/components/shipplay/content/FAQ'
import { faqItems } from '@/components/shipplay/content/faqItems'
import { Hero } from '@/components/shipplay/Hero'

import { cn } from '../../components/shipplay/ui/cn'

const PAGE_URL = 'https://www.shipplay.com/faq'

/**
 * schema.org FAQPage markup for the full question set — ported from the
 * playground FAQPage. Built from `answerText` (plain string) so answer engines
 * can consume it directly, and each question carries the same `#faq-<id>`
 * fragment the list items expose so crawlers can cite a single question.
 */
const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${PAGE_URL}#faq`,
  url: PAGE_URL,
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    '@id': `${PAGE_URL}#faq-${item.id}`,
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answerText,
    },
  })),
}

/**
 * Ship&Play "FAQ" page — hero + searchable FAQ list, ported from
 * playground-shipplay's FAQPage. Outer chrome (SiteUserBar / SiteNav /
 * ConciergeTeam / SiteFooter) comes from ClientLayout, matching the other
 * Ship&Play pages.
 *
 * SEO: renders every FAQ item (no `group` filter) so all 50+ questions land
 * in the static-export HTML. Also emits schema.org FAQPage JSON-LD keyed
 * off `answerText`, matching the playground FAQPage.
 */
const ShipplayFAQPage: React.FC = () => {
  return (
    <>
      {/* Escaping `<` keeps a stray `</script>` in the copy from closing
          the tag early. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqJsonLd).replace(/</g, '\\u003c'),
        }}
      />
      <Hero imgUrl="/images/shipplay/hero/desk-map.webp">
        <div
          className={cn(
            'snplay-text-bg-blur snplay:col-span-12',
            'snplay:sm:col-span-8 snplay:lg:col-span-6',
          )}
        >
          <h1
            className={cn(
              'snplay:2xl:text-7xl snplay:text-4xl snplay:font-bold',
              'snplay:leading-[1.2] snplay:md:text-5xl snplay:lg:text-6xl',
            )}
          >
            Frequently Asked <span className="snplay:text-primaryDark"> Questions</span>.
          </h1>
          <p className="snplay:mt-6 snplay:text-xl">
            Browse common queries or select a topic below to narrow your search.
          </p>
        </div>
      </Hero>
      <FAQ defaultItemsOpen />
    </>
  )
}

export default ShipplayFAQPage
